import { NO_FEDERAL_STATE, type FederalStateChoice } from "../constants/FederalStates";
import type { BookCatalogVolume } from "../networking/bookCatalog";
import { isBlockedByFilters, type BookFilters } from "./BookAvailability";
import { ISBN_QUERY_PATTERN, normalizeIsbn, toIsbn13 } from "./Isbn";

export interface CatalogBook {
    /** publisherId*bookId in the shop search's ids; not p6u's CombinedID. */
    id: string;
    bookName: string;
    publisherName: string;
    subjectId: string;
    subjectName: string;
    /** Only the volumes approved in the chosen state, sorted by name. */
    volumes: Array<BookCatalogVolume>;
    coverImage: string;
}

export interface CatalogLanguage {
    subjectId: string;
    subjectName: string;
    /** Sorted by name; never empty. */
    books: Array<CatalogBook>;
}

const compareNames = (a: string, b: string) => a.localeCompare(b, "de", { numeric: true, sensitivity: "base" });

/** The shop search's subject ids of the languages with the most books. */
const TOP_LANGUAGE_IDS = ["englisch", "french", "latin", "spanish"];

export const isTopLanguage = (language: CatalogLanguage): boolean => TOP_LANGUAGE_IDS.includes(language.subjectId);

/** Volumes without an approval on record count as approved everywhere, rather than as approved nowhere. */
const isVolumeApprovedIn = (volume: BookCatalogVolume, state: FederalStateChoice) =>
    state === NO_FEDERAL_STATE || volume.federalStates.length === 0 || volume.federalStates.includes(state);

/**
 * The catalog as the selection steps walk it: language → book → volume. A book shows up as soon as
 * one of its volumes is approved in the state, since a series is often approved volume by volume.
 */
interface BuildCatalogLanguagesOptions {
    volumes: Array<BookCatalogVolume>;
    state: FederalStateChoice;
    filters: BookFilters;
}

export const buildCatalogLanguages = ({
    volumes,
    state,
    filters,
}: BuildCatalogLanguagesOptions): Array<CatalogLanguage> => {
    const booksByKey = new Map<string, CatalogBook>();

    volumes
        .filter((volume) => !isBlockedByFilters(volume, filters) && isVolumeApprovedIn(volume, state))
        .forEach((volume) => {
            const id = `${volume.publisherId}*${volume.bookId}`;
            const key = `${volume.subjectId}|${id}`;
            const book = booksByKey.get(key);
            if (book) {
                book.volumes.push(volume);
            } else {
                booksByKey.set(key, {
                    id,
                    bookName: volume.bookName,
                    publisherName: volume.publisherName,
                    subjectId: volume.subjectId,
                    subjectName: volume.subjectName,
                    volumes: [volume],
                    coverImage: "",
                });
            }
        });

    const languagesById = new Map<string, CatalogLanguage>();
    booksByKey.forEach((book) => {
        book.volumes.sort((a, b) => compareNames(a.name, b.name));
        book.coverImage = book.volumes.find((volume) => volume.imageUrl)?.imageUrl ?? "";

        const language = languagesById.get(book.subjectId);
        if (language) {
            language.books.push(book);
        } else {
            languagesById.set(book.subjectId, {
                subjectId: book.subjectId,
                subjectName: book.subjectName,
                books: [book],
            });
        }
    });

    const languages = Array.from(languagesById.values());
    languages.forEach((language) => language.books.sort((a, b) => compareNames(a.bookName, b.bookName)));
    return languages.sort((a, b) => compareNames(a.subjectName, b.subjectName));
};

/** Whether a whole ISBN, written any way, belongs to the volume; an ISBN-10 matches by its ISBN-13. */
export const hasIsbn = (volume: BookCatalogVolume, isbn: string): boolean => {
    const needle = normalizeIsbn(toIsbn13(isbn) ?? isbn);
    return !!needle && volume.isbns.some((candidate) => normalizeIsbn(candidate) === needle);
};

// A few digits at least, so "Access 1" isn't read as one either.
const MIN_ISBN_QUERY_DIGITS = 3;

/**
 * Matches title and publisher, the volume names too, since teachers often know "Access 1" rather than the series,
 * and the volumes' ISBNs.
 */
export const searchCatalogBooks = (books: Array<CatalogBook>, query: string): Array<CatalogBook> => {
    const needle = query.trim().toLocaleLowerCase("de");
    if (!needle) {
        return books;
    }
    const isbnQuery = normalizeIsbn(needle);
    const isbnNeedle = ISBN_QUERY_PATTERN.test(needle) && isbnQuery.length >= MIN_ISBN_QUERY_DIGITS ? isbnQuery : "";

    return books.filter(
        (book) =>
            [book.bookName, book.publisherName, ...book.volumes.map((volume) => volume.fullName)].some((text) =>
                text.toLocaleLowerCase("de").includes(needle)
            ) ||
            (!!isbnNeedle &&
                book.volumes.some((volume) => volume.isbns.some((isbn) => normalizeIsbn(isbn).includes(isbnNeedle))))
    );
};

/** The first volume matching, with its book (all its volumes, whatever the state) and language. */
export const findCatalogVolume = (
    languages: Array<CatalogLanguage>,
    isMatch: (volume: BookCatalogVolume) => boolean
): { language: CatalogLanguage; book: CatalogBook; volume: BookCatalogVolume } | undefined => {
    for (const language of languages) {
        for (const book of language.books) {
            const volume = book.volumes.find(isMatch);
            if (volume) {
                return { language, book, volume };
            }
        }
    }
    return undefined;
};

const CLASS_PATTERN = /^(\d+)\.\s*(.+)$/;

/**
 * ["7. Klasse", "8. Klasse", "10. Klasse"] → "7.–8., 10. Klasse". The label is taken from the data, so
 * anything that doesn't follow "<number>. <label>" is listed as it is.
 */
export const formatClasses = (classes: Array<string>): string => {
    const matches = classes.map((entry) => CLASS_PATTERN.exec(entry.trim()));
    const label = matches[0]?.[2];
    if (!label || matches.some((match) => match?.[2] !== label)) {
        return classes.join(", ");
    }

    const grades = Array.from(new Set(matches.map((match) => Number(match![1])))).sort((a, b) => a - b);
    const runs: Array<string> = [];
    let runStart = grades[0];
    let runEnd = grades[0];
    grades.slice(1).forEach((grade) => {
        if (grade === runEnd + 1) {
            runEnd = grade;
            return;
        }
        runs.push(runStart === runEnd ? `${runStart}.` : `${runStart}.–${runEnd}.`);
        runStart = runEnd = grade;
    });
    runs.push(runStart === runEnd ? `${runStart}.` : `${runStart}.–${runEnd}.`);

    return `${runs.join(", ")} ${label}`;
};

/** The volume's classes, unless its name already states them, e.g. "Klasse 5" with ["5. Klasse"]. */
export const getVolumeClassesLabel = (volume: BookCatalogVolume): string => {
    const label = formatClasses(volume.schoolGrades);
    const matches = volume.schoolGrades.map((entry) => CLASS_PATTERN.exec(entry.trim()));
    const classWord = matches[0]?.[2];
    if (!label || !classWord || matches.some((match) => !match)) {
        return label;
    }

    const numbersInName: Array<string> = volume.name.match(/\d+/g) ?? [];
    const isStatedInName =
        volume.name.toLocaleLowerCase("de").includes(classWord.toLocaleLowerCase("de")) &&
        matches.every((match) => numbersInName.includes(match![1]));

    return isStatedInName ? "" : label;
};
